import * as React from "react";
import { useContext } from "react";

import { Skeleton } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import { FormType, formatNarrowCurrencyAmount, getDollarValue, AvailablePaymentCurrency } from "@simplyk/common";
import classNames from "classnames";

import { FrontendFormContext } from "../../contexts/FrontendFormContext";
import { useLocaleContext } from "../../contexts/LocaleContext";
import { trpc } from "../../helpers/trpc";
import { useTranslate } from "../../hooks/useTranslate";

import { useStyles } from "./useStyles";

import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

interface ThermometerProps {
  max: number | null | undefined;
  form: { id: string; formType: FormType };
  gutterBottom?: boolean;
  className?: Record<string, unknown>;
}

const Thermometer: React.FunctionComponent<React.PropsWithChildren<ThermometerProps>> = ({
  max,
  form,
  gutterBottom,
  className,
}) => {
  const classes = useStyles();
  const { t } = useTranslate();

  const { currency } = useContext(FrontendFormContext);
  const { isoLocale } = useLocaleContext();
  const { data: progressValue, isLoading: loading } = trpc.getFormVolume.useQuery({
    formId: form.id,
    formType: form.formType,
  });
  const volumeValue = progressValue || 0;

  if (loading) {
    return <Skeleton variant="rectangular" height={44} animation="wave" />;
  }

  return (
    <div className={classNames(className, classes.targetContainer, { [classes.gutterBottom]: gutterBottom })}>
      {max && (
        <>
          <div className={classes.targetTextContainer}>
            <DashboardTypography variant="h6" data-test="thermometer-progress">
              {formatNarrowCurrencyAmount(getDollarValue(volumeValue), {
                locale: isoLocale,
                currency: currency || AvailablePaymentCurrency.Usd,
              })}
            </DashboardTypography>
            <DashboardTypography variant="body1" display="inline" className={classes.targetText}>
              {t("donationForm", "of")}{" "}
            </DashboardTypography>
            <DashboardTypography variant="body1" display="inline" className={classes.targetText}>
              {formatNarrowCurrencyAmount(getDollarValue(max), {
                locale: isoLocale,
                currency: currency || AvailablePaymentCurrency.Usd,
              })}
            </DashboardTypography>
          </div>
          <LinearProgress
            color="primary"
            value={volumeValue > max ? 100 : (volumeValue / max) * 100}
            className={classes.termometre}
            variant="determinate"
          />
        </>
      )}
    </div>
  );
};

export default Thermometer;
